// src/lakehouse/ui/admin/src/features/datasets/components/QueryResultsTable.tsx
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/ui/data-table';
import { Badge } from '@/components/ui/badge';
import type { DatasetQueryResponse } from '../types/query.types';

export interface QueryResultsTableProps {
  response: DatasetQueryResponse | null;
  requestedColumns: string[];
  /**
   * DataTable seeds its internal pageSize state only ONCE at mount (useState
   * initialisation). QueryResultsTable is a persistent instance (no `key`
   * reset), so this value MUST be a constant ≥ the server's page size — using
   * rows.length would freeze the seed at the first result count and silently
   * truncate later, larger results. Default 10 000 = backend max `limit`.
   */
  pageSize?: number;
}

export function QueryResultsTable({ response, requestedColumns, pageSize = 10000 }: QueryResultsTableProps) {
  if (!response) {
    return (
      <div data-testid="results-empty" className="text-muted-foreground text-sm p-4">
        Run a query to see results.
      </div>
    );
  }

  const rows = response.data ?? [];

  const colNames =
    requestedColumns.length > 0
      ? requestedColumns
      : Array.from(
          rows.reduce((set, r) => {
            Object.keys(r).forEach((k) => set.add(k));
            return set;
          }, new Set<string>())
        );

  const columns: ColumnDef<Record<string, unknown>, unknown>[] = colNames.map((name) => ({
    accessorKey: name,
    header: name,
    cell: ({ row }) => {
      const v = row.original[name];
      return <span>{v === null || v === undefined ? '' : String(v)}</span>;
    },
  }));

  const m = response.metadata;

  return (
    <div className="space-y-3">
      <div
        data-testid="results-meta"
        className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground"
      >
        <span>
          {m.returned_rows} of {m.total_rows} rows
        </span>
        <span>·</span>
        <span>{m.execution_time_ms} ms</span>
        <span>·</span>
        <span>{m.filters_applied} filter(s) applied</span>
        {m.rls_filtered && <Badge variant="secondary">RLS filtered</Badge>}
        {m.masking_applied && <Badge variant="secondary">Masked</Badge>}
        {m.scan_capped && <Badge variant="destructive">Scan capped</Badge>}
      </div>
      <DataTable
        columns={columns}
        data={rows}
        hidePagination
        pageSize={pageSize}
      />
    </div>
  );
}
